import { useState } from 'react'
import type { Company, CompanyDashboardSummary } from '@/types/company.types'
import { Badge, Button, Modal } from '@/components/ui'
import { PermissionGate } from '@/components/common/PermissionGate'
import { EditCompanyForm } from '../EditCompanyForm'

export interface CompanyIdentityBandProps {
  company: Company
  summary?: CompanyDashboardSummary
  attendancePct: number | null
  isLoading: boolean
  hasError: boolean
}

/**
 * Who this is, and the three numbers that answer "is anything happening".
 *
 * The figures sit on one line rather than in a stat block: they are context for
 * the name, not the page's subject, and a bordered panel here would compete
 * with the attention list directly below it.
 */
export function CompanyIdentityBand({
  company,
  summary,
  attendancePct,
  isLoading,
  hasError,
}: CompanyIdentityBandProps): React.ReactElement {
  const [editOpen, setEditOpen] = useState(false)
  const memberTotal = summary?.memberCount ?? company.memberCount ?? 0
  const dash = isLoading ? '—' : null

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={company.status === 'active' ? 'success' : 'neutral'} dot>
            {company.status}
          </Badge>
          {company.code && (
            <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
              {company.code}
            </span>
          )}
          {hasError && (
            <Badge variant="danger" size="sm">
              Metrics unavailable
            </Badge>
          )}
        </div>

        <h2 className="mt-2 font-display text-[1.75rem] font-semibold leading-tight tracking-[-0.022em] text-gray-950 dark:text-gray-50 sm:text-[2rem]">
          {company.name}
        </h2>

        <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
          <span className="tabular-nums">
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {dash ?? memberTotal}
            </span>{' '}
            {memberTotal === 1 ? 'member' : 'members'}
          </span>
          <span aria-hidden className="text-gray-300 dark:text-gray-600">
            ·
          </span>
          <span className="tabular-nums">
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {dash ?? (summary?.clockedInNow ?? 0)}
            </span>{' '}
            clocked in now
          </span>
          <span aria-hidden className="text-gray-300 dark:text-gray-600">
            ·
          </span>
          <span
            className="tabular-nums"
            title="Share of staff due so far today (excluding leave) who attended"
          >
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {dash ?? (attendancePct === null ? '—' : `${attendancePct}%`)}
            </span>{' '}
            attendance today
          </span>
        </p>
      </div>

      <PermissionGate module="companies" action="update">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="shrink-0"
          onClick={() => setEditOpen(true)}
        >
          Edit company
        </Button>
      </PermissionGate>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Edit ${company.name}`}
        className="max-w-lg"
      >
        <EditCompanyForm
          company={company}
          onSuccess={() => setEditOpen(false)}
          onCancel={() => setEditOpen(false)}
        />
      </Modal>
    </div>
  )
}

CompanyIdentityBand.displayName = 'CompanyIdentityBand'
